import type { TOC } from '@ember/component/template-only';
import type { SafeString } from '@ember/template';

/**
 * Intent / color family
 */
export type CalloutIntent = 'brand' | 'success' | 'warning' | 'info' | 'danger';

interface CalloutSignature {
  Args: {
    /**
     * Visual intent — picks the token family (brand / success / warning /
     * info / danger). Defaults to `brand`.
     */
    intent?: CalloutIntent;
    /**
     * Swap the flat tinted fill for the original two-stop soft gradient
     * (preserves the pre-migration tinted softness, e.g. amber-50 →
     * orange-50 for warning). Defaults to `false`.
     */
    gradient?: boolean;
    /**
     * Use the saturated intent-gradient icon bubble with white iconography
     * (for promotional/CTA callouts where the icon should pop). Defaults
     * to `false` — uses the soft tinted bubble matching the intent body.
     */
    iconBold?: boolean;
    /**
     * Pull the callout up to overlap the element above it (used to stitch
     * a callout into the bottom of a hero). Applies `-mt-6 mx-6` with
     * `isolation: isolate` so the callout creates its own stacking
     * context and paints above the prior sibling without needing a
     * `z-index` value at the call site. Defaults to `false`.
     */
    overlap?: boolean;
    /**
     * Vertical alignment of the icon | content | action triad. Defaults
     * to `start` (matches `flex items-start`). Pass `center` for callouts
     * with single-line bodies.
     */
    align?: 'start' | 'center';
    /**
     * Optional title for the callout. Rendered as an `<h3>` carrying
     * `.tio-callout__title` (intent-driven color + bottom margin). Pass
     * a title here or render your own heading inside the default block —
     * not both.
     */
    title?: string | SafeString;
  };
  Blocks: {
    /**
     * Main content of the callout — body paragraphs, chips, anything
     * that isn't the icon or the action slot.
     */
    default: [];
    /**
     * Icon slot — typically a `<Ph…>` icon component. Wrapped in a
     * `.tio-callout__icon` (or `+ __icon--bold`) bubble.
     */
    icon?: [];
    /**
     * Trailing action — usually a `<Button>` (with `@route` when it navigates).
     * Renders to the right of the content on desktop.
     */
    action?: [];
  };
  Element: HTMLDivElement;
}

/**
 * Callout - Block-level tinted callout card
 *
 * Wraps the `.tio-callout` class family with a typed Glimmer API so call
 * sites can declare intent + variants via props instead of stacking
 * utility classes. Distinct from `<AlertBanner>` (inline strip with
 * `role="alert"` semantics) — `<Callout>` is a content card with no
 * implicit a11y role and supports a CTA slot.
 *
 * @example Brand promo with bold icon + action
 * ```gts
 * import Callout from 'tio-ui/components/callout';
 *
 * <Callout @intent="brand" @gradient={{true}} @iconBold={{true}}
 *          @overlap={{true}} @align="center" @title="Try Our AI Advisor">
 *   <:icon><PhSparkle @size={{28}} /></:icon>
 *   <:default>
 *     <p>Get instant answers to common questions 24/7.</p>
 *   </:default>
 *   <:action>
 *     <Button @intent="primary" @appearance="outlined">Chat Now</Button>
 *   </:action>
 * </Callout>
 * ```
 *
 * @example Inline success notice (no gradient, no icon bubble bold)
 * ```gts
 * <Callout @intent="success" @gradient={{true}} @align="center"
 *          @title="Good news!">
 *   <:icon><PhMagnifyingGlass @size={{24}} /></:icon>
 *   <:default>
 *     <p>You may qualify for additional assistance.</p>
 *   </:default>
 *   <:action>
 *     <Button @intent="primary" @size="sm">View Programs</Button>
 *   </:action>
 * </Callout>
 * ```
 */
const Callout: TOC<CalloutSignature> = <template>
  <div
    class="tio-callout tio-callout--{{if @intent @intent 'brand'}}
      {{if @gradient 'tio-callout--gradient'}}
      {{if @overlap 'tio-callout--overlap'}}
      {{if (eq @align 'center') 'items-center'}}"
    ...attributes
  >
    {{#if (has-block "icon")}}
      <div class="tio-callout__icon {{if @iconBold 'tio-callout__icon--bold'}}">
        {{yield to="icon"}}
      </div>
    {{/if}}
    <div class="flex-1">
      {{#if @title}}
        <h3 class="tio-callout__title">{{@title}}</h3>
      {{/if}}
      {{yield}}
    </div>
    {{#if (has-block "action")}}
      {{yield to="action"}}
    {{/if}}
  </div>
</template>;

export default Callout;
export type { CalloutSignature };
